// AnatomicalBodyMap.tsx — V3
// Compact body map embedded INSIDE the patient card.
// No wrapper card — just a silhouette with markers and a tiny legend.
// Supports four variants: male, female, kid, neutral.

import { Box, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { athenaColors } from "theme";
import BodySilhouette, { SilhouetteVariant } from "./BodySilhouette";
import { BODY_REGIONS, mapLocationToRegion } from "./bodyRegions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TableRow = Record<string, string | number | boolean | null | undefined>;

interface DomainTablesData {
  event: TableRow[];
  static: TableRow[];
}

interface UniversalTablesData {
  patientProfile: TableRow[];
  molecularProfile: TableRow[];
  hematologicalProfile: TableRow[];
  trialEnrollment: TableRow[];
  diagnoses?: TableRow[];
}

export type MarkerType = "primary" | "metastasis" | "recurrence";

export interface BodyMarker {
  region: string;
  type: MarkerType;
  label: string;
  detail: string;
  domain: string;
}

interface Props {
  domains: Record<string, DomainTablesData>;
  universal?: UniversalTablesData;
  /** When true, the small inline legend at the bottom is hidden */
  hideLegend?: boolean;
  /** When true, renders in a tight circle-avatar mode (no layout chrome, just the silhouette + markers) */
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Athena-themed marker colours
// ---------------------------------------------------------------------------

const MARKER_STYLES: Record<MarkerType, { color: string; label: string }> = {
  primary:    { color: athenaColors.softPurple, label: "Primary" },
  metastasis: { color: "#DE350B",              label: "Metastasis" },   // semantic error
  recurrence: { color: athenaColors.orange,    label: "Recurrence" },
};

// Compact mode markers — same as standard (light glass background is now used)
const MARKER_STYLES_COMPACT = MARKER_STYLES;

// ---------------------------------------------------------------------------
// Data extraction (same logic as V1, simplified)
// ---------------------------------------------------------------------------

function extractMarkers(
  domains: Record<string, DomainTablesData>,
  universal?: UniversalTablesData
): BodyMarker[] {
  const markers: BodyMarker[] = [];

  for (const [domainKey, tables] of Object.entries(domains)) {
    const shortDomain = domainKey
      .replace(/^medical\.cancer\./, "")
      .replace(/_/g, " ");

    // Static tables: primary site
    if (tables.static) {
      for (const row of tables.static) {
        for (const field of [
          "primary_site",
          "tumor_location",
          "tumor_quadrant",
        ]) {
          const val = row[field] as string | undefined;
          if (!val) continue;
          const region = mapLocationToRegion(val, domainKey);
          if (region) {
            markers.push({
              region,
              type: "primary",
              label: BODY_REGIONS[region]?.label || val,
              detail: fmt(val),
              domain: shortDomain,
            });
          }
        }
      }
    }

    // Event tables: metastasis / recurrence
    if (tables.event) {
      for (const row of tables.event) {
        const eventType = ((row.event_type as string) || "").toLowerCase();
        const location =
          (row.location as string) || (row.metastasis_site as string) || "";

        let markerType: MarkerType | null = null;
        if (
          eventType.includes("metastasis") ||
          eventType.includes("metastatic") ||
          eventType.includes("progression")
        ) {
          markerType = "metastasis";
        } else if (
          eventType.includes("recurrence") ||
          eventType.includes("relapse")
        ) {
          markerType = "recurrence";
        }
        if (!markerType) continue;

        let region = location ? mapLocationToRegion(location, domainKey) : null;
        if (!region) {
          const parts = eventType.split("_");
          for (let i = 1; i < parts.length; i++) {
            region = mapLocationToRegion(parts.slice(i).join("_"), domainKey);
            if (region) break;
          }
        }
        if (!region) region = mapLocationToRegion("", domainKey);
        if (region) {
          markers.push({
            region,
            type: markerType,
            label: BODY_REGIONS[region]?.label || location || eventType,
            detail: fmt(eventType),
            domain: shortDomain,
          });
        }
      }
    }
  }

  // Universal DiagnosisTable (FHIR anchor). One row per diagnosis; primary vs
  // metastasis is distinguished by `is_primary` (default true). A PRIMARY row
  // places its marker from `primary_site` (the primary tumour's origin); a
  // METASTASIS row (is_primary === false) uses the fine deposit location in
  // `metastasis_body_site`, falling back to the coarse `metastasis_site` bucket
  // when the fine site is null. Both map through the same site→region lookup.
  if (universal?.diagnoses) {
    for (const row of universal.diagnoses) {
      const isMetastasis = row.is_primary === false;
      const location = isMetastasis
        ? ((row.metastasis_body_site as string) ||
           (row.metastasis_site as string) ||
           "")
        : ((row.primary_site as string) || "");
      if (!location) continue;
      const region = mapLocationToRegion(location);
      if (!region) continue;
      const domainLabel = (
        (row.cancer_subtype as string) ||
        (row.cancer_type as string) ||
        ""
      ).replace(/_/g, " ");
      markers.push({
        region,
        type: isMetastasis ? "metastasis" : "primary",
        label: BODY_REGIONS[region]?.label || location,
        detail: fmt(location),
        domain: domainLabel,
      });
    }
  }

  return markers;
}

/**
 * Detect patient sex and age to determine silhouette variant.
 * Returns "kid" for patients under 18 based on date_of_birth or age fields.
 */
function detectVariant(universal?: UniversalTablesData): SilhouetteVariant {
  if (!universal?.patientProfile) return "neutral";

  let sex: "male" | "female" | null = null;
  let isChild = false;

  for (const row of universal.patientProfile) {
    // Detect sex/gender
    const sexVal = (
      (row.sex as string) ||
      (row.gender as string) ||
      ""
    ).toLowerCase();
    if (!sex) {
      if (sexVal === "male" || sexVal === "m") sex = "male";
      if (sexVal === "female" || sexVal === "f") sex = "female";
    }

    // Detect age — check explicit age field
    const ageVal = row.age ?? row.patient_age;
    if (ageVal !== undefined && ageVal !== null) {
      const numAge =
        typeof ageVal === "number" ? ageVal : parseInt(String(ageVal), 10);
      if (!isNaN(numAge) && numAge < 18) isChild = true;
    }

    // Detect age — check date_of_birth
    const dob =
      (row.date_of_birth as string) ||
      (row.dob as string) ||
      (row.birth_date as string) ||
      "";
    if (dob) {
      const birthDate = new Date(dob);
      if (!isNaN(birthDate.getTime())) {
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        if (age < 18) isChild = true;
      }
    }
  }

  if (isChild) return "kid";
  if (sex) return sex;
  return "neutral";
}

function fmt(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Marker SVG shapes — extra-large for easy hover/tap (r=12 in 100×150 viewBox)
// ---------------------------------------------------------------------------

function MarkerShape({
  cx,
  cy,
  type,
  color,
}: {
  cx: number;
  cy: number;
  type: MarkerType;
  color: string;
}) {
  const r = 12;
  const hitR = r + 5; // invisible hit area for easy hover
  switch (type) {
    case "primary":
      return (
        <>
          <circle cx={cx} cy={cy} r={hitR} fill="transparent" />
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={color}
            fillOpacity={0.8}
            stroke="white"
            strokeOpacity={0.5}
            strokeWidth={1}
          />
        </>
      );
    case "metastasis":
      return (
        <>
          <circle cx={cx} cy={cy} r={hitR} fill="transparent" />
          <polygon
            points={`${cx},${cy - r} ${cx - r},${cy + r * 0.7} ${cx + r},${
              cy + r * 0.7
            }`}
            fill={color}
            fillOpacity={0.8}
            stroke="white"
            strokeOpacity={0.5}
            strokeWidth={0.8}
            strokeLinejoin="round"
          />
        </>
      );
    case "recurrence":
      return (
        <>
          <circle cx={cx} cy={cy} r={hitR} fill="transparent" />
          <polygon
            points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${
              cx - r
            },${cy}`}
            fill={color}
            fillOpacity={0.8}
            stroke="white"
            strokeOpacity={0.5}
            strokeWidth={0.8}
            strokeLinejoin="round"
          />
        </>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnatomicalBodyMap: React.FC<Props> = ({
  domains,
  universal,
  hideLegend,
  compact,
}) => {
  const theme = useTheme();
  const markers = useMemo(
    () => extractMarkers(domains, universal),
    [domains, universal]
  );
  const silhouetteVariant = useMemo(
    () => detectVariant(universal),
    [universal]
  );

  // Always render the silhouette — markers are optional overlays
  // (The silhouette alone is valuable for showing patient gender/age)

  // Deduplicate by region+type
  const grouped = new Map<
    string,
    { marker: BodyMarker; count: number; details: string[] }
  >();
  for (const m of markers) {
    const key = `${m.region}:${m.type}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count++;
      if (!existing.details.includes(m.detail)) existing.details.push(m.detail);
    } else {
      grouped.set(key, { marker: m, count: 1, details: [m.detail] });
    }
  }

  // Offset same-region markers
  const regionSlots = new Map<string, number>();
  const entries = Array.from(grouped.values()).map((entry) => {
    const slot = regionSlots.get(entry.marker.region) || 0;
    regionSlots.set(entry.marker.region, slot + 1);
    return { ...entry, offsetX: slot * 4.5 };
  });

  // For kid variant, markers need to be transformed to match the scaled-down body
  const isKid = silhouetteVariant === "kid";

  // ---------------------------------------------------------------------------
  // Proximity clustering: group markers whose screen positions are close enough
  // that their hit areas overlap (e.g. liver + colon).  Each cluster gets a
  // single combined tooltip listing all markers in the group.
  // ---------------------------------------------------------------------------
  const PROXIMITY_THRESHOLD = 18; // ~2× marker radius; markers closer than this cluster

  type PositionedEntry = (typeof entries)[number] & { cx: number; cy: number };

  const positionedEntries: PositionedEntry[] = entries.map((e) => {
    const region = BODY_REGIONS[e.marker.region];
    if (!region) return { ...e, cx: -999, cy: -999 };
    const [cx, cy] = isKid
      ? [50 + (region.x + e.offsetX - 50) * 0.75, 75 + (region.y - 75) * 0.75]
      : [region.x + e.offsetX, region.y];
    return { ...e, cx, cy };
  });

  // Union-find clustering by proximity
  const clusterOf = positionedEntries.map((_, i) => i);
  function findRoot(i: number): number {
    while (clusterOf[i] !== i) {
      clusterOf[i] = clusterOf[clusterOf[i]];
      i = clusterOf[i];
    }
    return i;
  }
  for (let i = 0; i < positionedEntries.length; i++) {
    for (let j = i + 1; j < positionedEntries.length; j++) {
      const dx = positionedEntries[i].cx - positionedEntries[j].cx;
      const dy = positionedEntries[i].cy - positionedEntries[j].cy;
      if (Math.sqrt(dx * dx + dy * dy) < PROXIMITY_THRESHOLD) {
        clusterOf[findRoot(j)] = findRoot(i);
      }
    }
  }
  // Build cluster groups
  const clusterMap = new Map<number, number[]>();
  for (let i = 0; i < positionedEntries.length; i++) {
    const root = findRoot(i);
    if (!clusterMap.has(root)) clusterMap.set(root, []);
    clusterMap.get(root)!.push(i);
  }
  // Pre-compute which entries are clustered (size > 1)
  const clusteredIndices = new Set<number>();
  const clusters: { indices: number[]; cx: number; cy: number }[] = [];
  for (const [, indices] of clusterMap) {
    if (indices.length > 1) {
      indices.forEach((i) => clusteredIndices.add(i));
      // Cluster centroid for the shared tooltip hit area
      const cx = indices.reduce((s, i) => s + positionedEntries[i].cx, 0) / indices.length;
      const cy = indices.reduce((s, i) => s + positionedEntries[i].cy, 0) / indices.length;
      clusters.push({ indices, cx, cy });
    }
  }

  // Which marker types are present?
  const activeTypes = [...new Set(markers.map((m) => m.type))];

  // Periwinkle color from theme — matches navbar icons
  const periwinkle = theme.palette.secondary.main;

  // Compact mode: tight silhouette + markers for avatar circle (with tooltips)
  if (compact) {
    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <Box
          sx={{
            position: "absolute",
            inset: "4%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <BodySilhouette
            variant={silhouetteVariant}
            lightMode
            strokeColor={periwinkle}
            style={{ height: "100%", width: "auto", opacity: 0.9 }}
          />
        </Box>
        <svg
          viewBox="0 0 100 150"
          style={{
            position: "absolute",
            inset: "4%",
            width: "92%",
            height: "92%",
            pointerEvents: "none",
          }}
        >
          {/* Standalone markers (compact) */}
          {positionedEntries.map((pe, idx) => {
            if (clusteredIndices.has(idx)) return null;
            const region = BODY_REGIONS[pe.marker.region];
            if (!region) return null;
            const markerStyle = MARKER_STYLES_COMPACT[pe.marker.type];
            return (
              <g key={`m-${idx}`} style={{ pointerEvents: "auto", cursor: "pointer" }}>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                        {region.label}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: "capitalize", display: "block" }}>
                        {pe.marker.type}{pe.count > 1 ? ` (${pe.count})` : ""}
                      </Typography>
                      {pe.details.map((d, i) => (
                        <Typography key={i} variant="caption" sx={{ display: "block", fontSize: 10, opacity: 0.6 }}>
                          {d} - {fmt(pe.marker.domain)}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="right"
                  slotProps={{ tooltip: { sx: { backdropFilter: "blur(12px)", borderRadius: 1, boxShadow: "0 4px 24px rgba(0,0,0,0.2)" } } }}
                >
                  <g>
                    <MarkerShape cx={pe.cx} cy={pe.cy} type={pe.marker.type} color={markerStyle.color} />
                  </g>
                </Tooltip>
              </g>
            );
          })}

          {/* Clustered markers (compact) */}
          {clusters.map((cluster, ci) => {
            const tooltipContent = (
              <Box sx={{ p: 0.25 }}>
                {cluster.indices.map((idx, mi) => {
                  const pe = positionedEntries[idx];
                  const region = BODY_REGIONS[pe.marker.region];
                  return (
                    <Box key={mi} sx={mi > 0 ? { mt: 0.75, pt: 0.75, borderTop: "1px solid rgba(255,255,255,0.15)" } : undefined}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                        {region?.label ?? pe.marker.region}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: "capitalize", display: "block" }}>
                        {pe.marker.type}{pe.count > 1 ? ` (${pe.count})` : ""}
                      </Typography>
                      {pe.details.map((d, di) => (
                        <Typography key={di} variant="caption" sx={{ display: "block", fontSize: 10, opacity: 0.6 }}>
                          {d} - {fmt(pe.marker.domain)}
                        </Typography>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            );
            return (
              <g key={`cl-${ci}`} style={{ pointerEvents: "auto", cursor: "pointer" }}>
                <Tooltip
                  title={tooltipContent}
                  arrow
                  placement="right"
                  slotProps={{ tooltip: { sx: { backdropFilter: "blur(12px)", borderRadius: 1, boxShadow: "0 4px 24px rgba(0,0,0,0.2)" } } }}
                >
                  <g>
                    <circle cx={cluster.cx} cy={cluster.cy} r={PROXIMITY_THRESHOLD} fill="transparent" />
                    {cluster.indices.map((idx) => {
                      const pe = positionedEntries[idx];
                      const markerStyle = MARKER_STYLES_COMPACT[pe.marker.type];
                      return (
                        <MarkerShape key={idx} cx={pe.cx} cy={pe.cy} type={pe.marker.type} color={markerStyle.color} />
                      );
                    })}
                  </g>
                </Tooltip>
              </g>
            );
          })}
        </svg>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
      }}
    >
      {/* Silhouette + Markers */}
      <Box sx={{ position: "relative", flex: 1, width: "100%", minHeight: 0 }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <BodySilhouette
            variant={silhouetteVariant}
            lightMode
            strokeColor={periwinkle}
            style={{ height: "100%", width: "auto", opacity: 0.9 }}
          />
        </Box>

        <svg
          viewBox="0 0 100 150"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {/* Render standalone markers (not in a proximity cluster) */}
          {positionedEntries.map((pe, idx) => {
            if (clusteredIndices.has(idx)) return null;
            const region = BODY_REGIONS[pe.marker.region];
            if (!region) return null;
            const style = MARKER_STYLES[pe.marker.type];
            return (
              <g key={`m-${idx}`} style={{ pointerEvents: "auto", cursor: "pointer" }}>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                        {region.label}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: "capitalize", display: "block" }}>
                        {pe.marker.type}{pe.count > 1 ? ` (${pe.count})` : ""}
                      </Typography>
                      {pe.details.map((d, i) => (
                        <Typography key={i} variant="caption" sx={{ display: "block", fontSize: 10, opacity: 0.6 }}>
                          {d} - {fmt(pe.marker.domain)}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="left"
                  slotProps={{ tooltip: { sx: { backdropFilter: "blur(12px)", borderRadius: 1, boxShadow: "0 4px 24px rgba(0,0,0,0.2)" } } }}
                >
                  <g>
                    <MarkerShape cx={pe.cx} cy={pe.cy} type={pe.marker.type} color={style.color} />
                  </g>
                </Tooltip>
              </g>
            );
          })}

          {/* Render proximity clusters — individual markers + shared combined tooltip */}
          {clusters.map((cluster, ci) => {
            // Build combined tooltip content for the entire cluster
            const tooltipContent = (
              <Box sx={{ p: 0.25 }}>
                {cluster.indices.map((idx, mi) => {
                  const pe = positionedEntries[idx];
                  const region = BODY_REGIONS[pe.marker.region];
                  return (
                    <Box key={mi} sx={mi > 0 ? { mt: 0.75, pt: 0.75, borderTop: "1px solid rgba(255,255,255,0.15)" } : undefined}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                        {region?.label ?? pe.marker.region}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: "capitalize", display: "block" }}>
                        {pe.marker.type}{pe.count > 1 ? ` (${pe.count})` : ""}
                      </Typography>
                      {pe.details.map((d, di) => (
                        <Typography key={di} variant="caption" sx={{ display: "block", fontSize: 10, opacity: 0.6 }}>
                          {d} - {fmt(pe.marker.domain)}
                        </Typography>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            );

            return (
              <g key={`cl-${ci}`} style={{ pointerEvents: "auto", cursor: "pointer" }}>
                <Tooltip
                  title={tooltipContent}
                  arrow
                  placement="left"
                  slotProps={{ tooltip: { sx: { backdropFilter: "blur(12px)", borderRadius: 1, boxShadow: "0 4px 24px rgba(0,0,0,0.2)" } } }}
                >
                  <g>
                    {/* Invisible hit area covering the entire cluster */}
                    <circle cx={cluster.cx} cy={cluster.cy} r={PROXIMITY_THRESHOLD} fill="transparent" />
                    {/* Draw each marker at its actual position */}
                    {cluster.indices.map((idx) => {
                      const pe = positionedEntries[idx];
                      const style = MARKER_STYLES[pe.marker.type];
                      return (
                        <MarkerShape key={idx} cx={pe.cx} cy={pe.cy} type={pe.marker.type} color={style.color} />
                      );
                    })}
                  </g>
                </Tooltip>
              </g>
            );
          })}
        </svg>
      </Box>

      {/* Tiny legend (hidden when hideLegend is true) */}
      {!hideLegend && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            pt: 0.5,
            flexShrink: 0,
          }}
        >
          {activeTypes.map((t) => (
            <Box
              key={t}
              sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius:
                    t === "primary" ? "50%" : t === "recurrence" ? 0 : "1px",
                  bgcolor: MARKER_STYLES[t].color,
                  opacity: 0.75,
                  transform: t === "recurrence" ? "rotate(45deg)" : undefined,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 8,
                  lineHeight: 1,
                  color: alpha(
                    theme.custom?.contentText?.muted ||
                      theme.palette.text.secondary,
                    0.8
                  ),
                }}
              >
                {MARKER_STYLES[t].label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AnatomicalBodyMap;
